const PRODUCT_GID_PREFIX = "gid://shopify/Product/";
const VARIANT_GID_PREFIX = "gid://shopify/ProductVariant/";
const SUBSCRIPTION_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const PRODUCT_HANDLE = /^[a-z0-9][a-z0-9-]*$/;

export const BACK_IN_STOCK_ENDPOINT = "/apps/consultant/api/extension/back-in-stock/subscriptions";
export const BACK_IN_STOCK_LOCALE = "en-ZA";
export const BACK_IN_STOCK_CONSENT_VERSION = "bis-v1";

function requireGid(value, prefix, label) {
  const normalized = String(value || "").trim();
  const suffix = normalized.slice(prefix.length);

  if (!normalized.startsWith(prefix) || !/^\d+$/.test(suffix)) {
    throw new TypeError(`${label} must be an exact Shopify GID.`);
  }

  return normalized;
}

export function buildCapturePayload({ productId, variantId, email, name }) {
  const payload = {
    shopify_product_id: requireGid(productId, PRODUCT_GID_PREFIX, "productId"),
    shopify_variant_id: requireGid(variantId, VARIANT_GID_PREFIX, "variantId"),
    locale: BACK_IN_STOCK_LOCALE,
    consent_text_version: BACK_IN_STOCK_CONSENT_VERSION,
  };

  const guestEmail = String(email || "").trim();
  const guestName = String(name || "").trim();

  if (guestEmail) payload.email = guestEmail;
  if (guestName) payload.name = guestName;

  return payload;
}

export function captureFeedback(httpStatus, responseStatus) {
  if (httpStatus >= 200 && httpStatus < 300) {
    if (responseStatus === "verification_required") {
      return {
        kind: "success",
        message: "Check your email to confirm this stock notification request.",
      };
    }

    return {
      kind: "success",
      message: "Your stock notification request is active.",
    };
  }

  if (httpStatus === 409) {
    return {
      kind: "notice",
      message: "This item is available now. Refresh the page before trying again.",
    };
  }

  if (httpStatus === 429) {
    return {
      kind: "error",
      message: "Too many requests. Please wait a while and try again.",
    };
  }

  return {
    kind: "error",
    message: "We could not save your request. Please refresh the page and try again.",
  };
}

export function normalizeSubscription(value) {
  if (!value || typeof value !== "object") return null;

  const subscriptionId = String(value.subscription_id || "").trim();
  if (!SUBSCRIPTION_ID.test(subscriptionId)) return null;

  return {
    subscription_id: subscriptionId,
    shopify_product_id: String(value.shopify_product_id || "").trim(),
    shopify_variant_id: String(value.shopify_variant_id || "").trim(),
    sku: String(value.sku || "").trim(),
    product_title: String(value.product_title || "Product unavailable").trim() || "Product unavailable",
    variant_title: String(value.variant_title || "").trim(),
    product_handle: String(value.product_handle || "").trim(),
    status: String(value.status || "unknown").trim().toLowerCase(),
    created_at: String(value.created_at || "").trim(),
    expires_at: String(value.expires_at || "").trim(),
    can_cancel: value.can_cancel === true,
  };
}

export function normalizeSubscriptions(payload) {
  const rows = Array.isArray(payload?.subscriptions) ? payload.subscriptions : [];
  return rows.map(normalizeSubscription).filter(Boolean);
}

export function subscriptionProductPath(subscription) {
  const handle = String(subscription?.product_handle || "").trim().toLowerCase();
  const variantGid = String(subscription?.shopify_variant_id || "").trim();
  const variantId = variantGid.startsWith(VARIANT_GID_PREFIX)
    ? variantGid.slice(VARIANT_GID_PREFIX.length)
    : "";

  if (!PRODUCT_HANDLE.test(handle) || !/^\d+$/.test(variantId)) return null;
  return `/products/${encodeURIComponent(handle)}?variant=${encodeURIComponent(variantId)}`;
}

export function subscriptionStatusLabel(status) {
  return ({
    pending_verification: "Awaiting email confirmation",
    active: "Waiting for restock",
    queued: "Restock detected",
    sent: "Notification sent",
    suppressed: "Notification not sent",
    cancelled: "Cancelled",
    expired: "Expired",
    held: "Needs review",
    failed: "Delivery failed",
  })[status] || "Status unavailable";
}

async function readJson(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

const HTMLElementBase = globalThis.HTMLElement || class {};

class DbmBackInStockCapture extends HTMLElementBase {
  connectedCallback() {
    this.abortController = new AbortController();
    this.form = document.getElementById(this.dataset.formId);
    this.status = this.querySelector("[data-bis-status]");
    this.button = this.querySelector(".dbm-bis-capture__submit");
    this.controls = this.querySelector("[data-bis-capture-controls]");

    if (!this.form || !this.status || !this.button || !this.controls) return;
    this.button.addEventListener("click", (event) => this.submit(event), {
      signal: this.abortController.signal,
    });
    this.addEventListener("keydown", (event) => this.submitOnEnter(event), {
      signal: this.abortController.signal,
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  submitOnEnter(event) {
    if (event.key !== "Enter" || event.target === this.button) return;
    event.preventDefault();
    this.submit(event);
  }

  async submit(event) {
    event.preventDefault();
    if (!this.form.reportValidity()) return;

    const button = this.button;
    const formData = new FormData(this.form);
    let payload;

    try {
      payload = buildCapturePayload({
        productId: this.dataset.productId,
        variantId: this.dataset.variantId,
        email: this.dataset.signedIn === "true" ? "" : formData.get("email"),
        name: this.dataset.signedIn === "true" ? "" : formData.get("name"),
      });
    } catch {
      this.showFeedback(captureFeedback(422));
      return;
    }

    button?.setAttribute("aria-busy", "true");
    if (button) button.disabled = true;

    try {
      const response = await fetch(this.dataset.endpoint || BACK_IN_STOCK_ENDPOINT, {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
        signal: this.abortController.signal,
      });
      const body = await readJson(response);
      const feedback = captureFeedback(response.status, body?.status);
      this.showFeedback(feedback);

      if (response.ok || response.status === 409) {
        this.controls.hidden = true;
      }
    } catch (error) {
      if (error?.name !== "AbortError") this.showFeedback(captureFeedback(500));
    } finally {
      button?.removeAttribute("aria-busy");
      if (button && !this.controls.hidden) button.disabled = false;
    }
  }

  showFeedback(feedback) {
    this.status.hidden = false;
    this.status.dataset.kind = feedback.kind;
    this.status.textContent = feedback.message;
  }
}

function formattedDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-ZA", { dateStyle: "medium" }).format(date);
}

function element(tagName, className, text) {
  const node = document.createElement(tagName);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

class DbmBackInStockList extends HTMLElementBase {
  connectedCallback() {
    this.abortController = new AbortController();
    this.endpoint = this.dataset.endpoint || BACK_IN_STOCK_ENDPOINT;
    this.load();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  async load(notice = "") {
    this.renderMessage("loading", "Loading your stock notifications…");

    try {
      const response = await fetch(this.endpoint, {
        credentials: "include",
        headers: { Accept: "application/json" },
        signal: this.abortController.signal,
      });

      if (response.status === 401) {
        this.renderMessage("error", "Please sign in again to view your stock notifications.");
        return;
      }

      if (!response.ok) {
        this.renderMessage("error", "Stock notifications are temporarily unavailable. Please try again later.");
        return;
      }

      const rows = normalizeSubscriptions(await readJson(response));
      this.renderRows(rows, notice);
    } catch (error) {
      if (error?.name !== "AbortError") {
        this.renderMessage("error", "Stock notifications are temporarily unavailable. Please try again later.");
      }
    }
  }

  renderMessage(kind, message) {
    const panel = element("div", "dbm-bis-list__message", message);
    panel.dataset.kind = kind;
    panel.setAttribute("role", kind === "error" ? "alert" : "status");
    this.replaceChildren(panel);
  }

  renderRows(rows, notice) {
    const fragment = document.createDocumentFragment();

    if (notice) {
      const noticePanel = element("p", "dbm-bis-list__notice", notice);
      noticePanel.setAttribute("role", "status");
      fragment.append(noticePanel);
    }

    if (rows.length === 0) {
      const empty = element("div", "dbm-bis-list__empty");
      empty.append(
        element("h2", "dbm-bis-list__empty-title", "No stock notifications yet"),
        element("p", "text-subdued", "When an item is sold out, use “Notify me when back in stock” on its product page."),
      );
      const browse = element("a", "button button--sm", "Browse products");
      browse.href = "/collections/all";
      empty.append(browse);
      fragment.append(empty);
      this.replaceChildren(fragment);
      return;
    }

    const grid = element("div", "dbm-bis-list__grid");
    rows.forEach((row) => grid.append(this.renderRow(row)));
    fragment.append(grid);
    this.replaceChildren(fragment);
  }

  renderRow(row) {
    const card = element("article", "dbm-bis-row");
    const marker = element("span", "dbm-bis-row__marker", "↻");
    marker.setAttribute("aria-hidden", "true");
    card.append(marker);

    const body = element("div", "dbm-bis-row__body");
    const productPath = subscriptionProductPath(row);
    const title = productPath
      ? element("a", "dbm-bis-row__title", row.product_title)
      : element("span", "dbm-bis-row__title", row.product_title);
    if (productPath) title.href = productPath;
    body.append(title);

    if (row.variant_title && row.variant_title !== "Default Title") {
      body.append(element("p", "text-subdued text-sm", row.variant_title));
    }

    const status = element("span", "dbm-bis-row__status", subscriptionStatusLabel(row.status));
    status.dataset.status = row.status;
    body.append(status);

    const dates = [
      formattedDate(row.created_at) ? `Requested ${formattedDate(row.created_at)}` : "",
      formattedDate(row.expires_at) ? `Expires ${formattedDate(row.expires_at)}` : "",
    ].filter(Boolean).join(" · ");
    if (dates) body.append(element("p", "text-subdued text-xs", dates));

    if (!productPath) {
      body.append(element("p", "text-subdued text-xs", "The exact product variant is no longer available to open."));
    }

    card.append(body);

    if (row.can_cancel) {
      const cancel = element("button", "button button--outline button--sm", "Cancel");
      cancel.type = "button";
      cancel.setAttribute("aria-label", `Cancel stock notification for ${row.product_title}`);
      cancel.addEventListener("click", () => this.cancel(row, cancel));
      card.append(cancel);
    }

    return card;
  }

  async cancel(row, button) {
    button.disabled = true;
    button.setAttribute("aria-busy", "true");

    try {
      const response = await fetch(`${this.endpoint}/${encodeURIComponent(row.subscription_id)}`, {
        method: "DELETE",
        credentials: "include",
        headers: { Accept: "application/json" },
        signal: this.abortController.signal,
      });

      if (!response.ok && response.status !== 404) {
        throw new Error(`Cancellation failed with ${response.status}`);
      }

      const body = await readJson(response);
      const notice = response.status === 202 || body?.status === "reconciliation_pending"
        ? "Cancellation is being finalized. The authoritative status is shown below."
        : "Stock notification cancelled.";
      await this.load(notice);
    } catch (error) {
      if (error?.name !== "AbortError") {
        button.disabled = false;
        button.removeAttribute("aria-busy");
        button.parentNode?.querySelector(".dbm-bis-row__cancel-error")?.remove();
        const failure = element("p", "dbm-bis-row__cancel-error", "Could not cancel. Please try again.");
        failure.setAttribute("role", "alert");
        button.parentNode?.append(failure);
      }
    }
  }
}

if (globalThis.customElements && !customElements.get("dbm-back-in-stock")) {
  customElements.define("dbm-back-in-stock", DbmBackInStockCapture);
}

if (globalThis.customElements && !customElements.get("dbm-back-in-stock-list")) {
  customElements.define("dbm-back-in-stock-list", DbmBackInStockList);
}
